import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  agentHealthQuery,
  agentHealthSeriesQuery,
  forceAgentUpdate,
  retrieveAgentLogs,
  saveAgentAutoUpdate,
  type AgentHealthRange,
  type DeviceAgentHealth as HealthPage,
} from '../api/devices'
import { DatePicker } from '../ui/DatePicker'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import { AgentMonitorChart } from './AgentMonitorChart'
import { DeviceApiServer } from './DeviceApiServer'

/**
 * Fila Health - agentResourceConsuming.php: cardul Status, dalele Last CPU /
 * Last Memory, ferestrele 1h/24h/7 days/30 days/Custom si cele doua grafice.
 * Force update (BAT apoi UPD) si Retrieve Debug Logs (LOG) sunt aici.
 */

const HEALTH_CSS = `.agent-monitor-wrap .stat-tile{background:#f7f9fb;border:1px solid #e3e8ee;border-radius:8px;padding:16px 18px;margin-bottom:12px;min-height:88px}
.agent-monitor-wrap .stat-tile .label-muted{color:#6b7a8c;font-size:12px;text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px}
.agent-monitor-wrap .stat-tile .stat-value{font-size:28px;font-weight:600;line-height:1.1;color:#1f2d3d}
.agent-monitor-wrap .stat-tile .stat-sub{color:#8a97a8;font-size:12px;margin-top:4px}
.agent-monitor-wrap .chart-card{border:1px solid #e3e8ee;border-radius:8px;padding:16px 18px 8px;margin-bottom:14px;background:#fff}
.agent-monitor-wrap .chart-card h5{margin:0 0 10px;font-size:14px;color:#3a4a5b}
.agent-monitor-wrap .side-card{border:1px solid #e3e8ee;border-radius:8px;padding:16px 18px;margin-bottom:16px;background:#fff}
.agent-monitor-wrap .side-card h4{margin-top:0;margin-bottom:12px;font-size:15px}
.agent-monitor-wrap .agent-facts{margin:0;font-size:13px}
.agent-monitor-wrap .agent-facts dt{float:left;clear:left;width:42%;color:#6b7a8c;font-weight:normal;padding:5px 0}
.agent-monitor-wrap .agent-facts dd{margin-left:42%;padding:5px 0;color:#1f2d3d;border-bottom:1px solid #eef1f5}
.agent-monitor-wrap .agent-facts dd:last-child{border-bottom:0}
.agent-monitor-wrap .agent-facts-sub{color:#8a97a8;font-size:12px}
.agent-monitor-wrap .agent-pending{margin-top:12px;padding:9px 11px;border:1px solid #e3e8ee;border-left:3px solid #c2913f;border-radius:4px;background:#fdfaf3;font-size:13px;color:#5a4a2a}
.agent-monitor-wrap .agent-auto-update{margin-top:14px;padding-top:12px;border-top:1px solid #e3e8ee;font-size:13px}
.agent-monitor-wrap .agent-auto-update label{font-weight:normal;margin:0;cursor:pointer}
.agent-monitor-wrap .agent-auto-update input{margin-right:6px}
.agent-monitor-wrap .agent-auto-update-state{margin-left:8px;font-size:12px;color:#8a97a8}`

const RANGES: { key: AgentHealthRange; label: string }[] = [
  { key: '1h', label: '1h' },
  { key: '24h', label: '24h' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'custom', label: 'Custom' },
]

function today(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function DeviceAgentHealth({ id }: { id: number }) {
  const health = useQuery(agentHealthQuery(id))
  const [range, setRange] = useState<AgentHealthRange>('24h')
  const [custom, setCustom] = useState(today)
  const selected = range === 'custom' ? custom : range
  const series = useQuery(agentHealthSeriesQuery(id, selected))

  if (health.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {health.error.message}
      </div>
    )
  }
  if (!health.data) return <Spinner />

  return (
    <div className="row agent-monitor-wrap">
      <style>{HEALTH_CSS}</style>
      <div className="col-md-4">
        <Status page={health.data} />
        <RetrieveLogs id={health.data.device.id} />
        <DeviceApiServer id={health.data.device.id} />
      </div>
      <div className="col-md-8">
        <div className="row">
          <div className="col-sm-6">
            <div className="stat-tile">
              <div className="label-muted">Agent CPU</div>
              <div className="stat-value">{health.data.cpuLabel}</div>
              <div className="stat-sub">{health.data.lastSample.at ?? 'No sample yet'}</div>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="stat-tile">
              <div className="label-muted">Agent Memory</div>
              <div className="stat-value">{health.data.memLabel}</div>
              <div className="stat-sub">{health.data.lastSample.at ?? 'No sample yet'}</div>
            </div>
          </div>
        </div>
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="col-md-12">
            <h4 className="bordered" style={{ marginTop: 4 }}>
              Resource history
            </h4>
          </div>
          <div className="col-md-7">
            <div className="btn-group btn-group-sm agent-monitor-range" role="group" aria-label="Range">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={`btn btn-default${range === r.key ? ' active' : ''}`}
                  data-range={r.key}
                  onClick={() => setRange(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="col-md-5">
            <div className="agent-monitor-custom" style={{ display: range === 'custom' ? undefined : 'none' }}>
              <DatePicker value={custom} name="agent-monitor-date" icon="far fa-calendar-check ico" onChange={setCustom} />
            </div>
          </div>
        </div>
        <div className="chart-card">
          <h5>
            Historic <strong>Agent CPU</strong>
          </h5>
          <div style={{ height: 180 }} id="agent_cpu_consuming">
            {series.error ? (
              <div className="alert alert-danger" style={{ margin: 0 }}>
                Could not load CPU history.
              </div>
            ) : series.data ? (
              <AgentMonitorChart what="CPU" unit="%" range={selected} points={series.data.points} />
            ) : (
              'Loading...'
            )}
          </div>
        </div>
        <div className="chart-card">
          <h5>
            Historic <strong>Agent Memory</strong>
          </h5>
          <div style={{ height: 180 }} id="agent_memory_consuming">
            {series.error ? (
              <div className="alert alert-danger" style={{ margin: 0 }}>
                Could not load memory history.
              </div>
            ) : series.data ? (
              <AgentMonitorChart what="memory" unit="" range={selected} points={series.data.points} memory />
            ) : (
              'Loading...'
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Status({ page }: { page: HealthPage }) {
  const query = useQueryClient()
  const [saving, setSaving] = useState('')
  const on = page.updateMode > 0
  const modeOn = page.updateMode === 2 ? 2 : 1

  const forceUpdate = async (target: HealthPage) => {
    if (!window.confirm('Force update?\n\nYou will not be able to undo this action!')) return
    try {
      const res = await forceAgentUpdate(target.device.id, target.target)
      if (res.status === 'ERROR') {
        toast(res.version ? String(res.version) : 'You do not have rights to update this agent.', 'Error', 'error')
        return
      }
      toast('Instructions sent', 'Force update')
      await query.invalidateQueries({ queryKey: ['device', target.device.id, 'agent-settings', 'health'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'You do not have rights to update this agent.', 'Error', 'error')
    }
  }

  const toggle = async (checked: boolean) => {
    const wanted = checked ? modeOn : 0
    setSaving('saving…')
    try {
      await saveAgentAutoUpdate(page.device.id, wanted)
      await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agent-settings', 'health'] })
      setSaving('saved')
      window.setTimeout(() => setSaving(''), 2000)
    } catch {
      setSaving('')
      toast('Auto-update could not be changed.', 'Error', 'error')
    }
  }

  return (
    <div className="side-card">
      <h4>
        <i className="fas fa-heartbeat"></i> Status
      </h4>
      <Banner page={page} />
      <dl className="agent-facts">
        <dt>Version</dt>
        <dd>
          {page.installed !== '' ? page.installed : <>&mdash;</>}{' '}
          {page.lastAgentUpdate ? (
            <span className="agent-facts-sub">updated {page.lastAgentUpdate.slice(0, 16)}</span>
          ) : (
            <span className="agent-facts-sub" title="Counted from the first update onwards">
              no update recorded yet
            </span>
          )}
        </dd>
        <dt>Latest version</dt>
        <dd>
          {page.target !== '' ? (
            page.target
          ) : page.isLinux ? (
            <span className="agent-facts-sub">no Linux agent uploaded yet</span>
          ) : (
            <span className="agent-facts-sub">not set for this company</span>
          )}
        </dd>
        <dt>Last report</dt>
        <dd>
          {page.lastSeen !== '' ? page.lastSeen.slice(0, 16) : <span className="agent-facts-sub">never</span>}
        </dd>
      </dl>
      {page.forceUpdatePending ? (
        <div className="agent-pending">
          <i className="fas fa-hourglass-half"></i> Update to {page.forceUpdateVersion} already requested
          <span className="agent-facts-sub"> &mdash; auto within 24h, force is immediate</span>
        </div>
      ) : null}
      {page.canUpdate ? (
        <button
          type="button"
          className="btn btn-theme btn-block"
          style={{ marginTop: 12 }}
          onClick={() => void forceUpdate(page)}
        >
          <i className="far fa-arrow-alt-circle-up"></i> Update to {page.target}
        </button>
      ) : null}
      <div className="agent-auto-update">
        <SettingsCheck
          className="agentAutoUpdate"
          data-device={page.device.id}
          data-on={modeOn}
          checked={on}
          disabled={saving === 'saving…'}
          onChange={(next) => void toggle(next)}
          label={
            <>
              Auto-update
              {page.updateMode === 2 && !page.isLinux ? <span className="agent-facts-sub">(beta)</span> : null}
            </>
          }
        />
        <span className="agent-auto-update-state">{saving}</span>
      </div>
    </div>
  )
}

function RetrieveLogs({ id }: { id: number }) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)

  const send = async () => {
    if (!window.confirm('Are you sure?')) return
    setBusy(true)
    try {
      await retrieveAgentLogs(id, email)
      setEmail('')
      toast('Request sent!', 'Success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Email address invalid', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="side-card">
      <h4>
        <i className="fas fa-file-download"></i> Retrieve Debug Logs
      </h4>
      <p className="text-muted" style={{ fontSize: 11.5, margin: '0 0 10px' }}>
        The agent mails the logs to this address. Uploading them straight into the log below needs an agent that posts
        them to the portal; until then, the address is how they get out of the machine.
      </p>
      <form
        className="labelcustomize retrieveLogs"
        data-collabel="3"
        data-alignlabel="left"
        data-label="color"
        method="post"
        action="/overview/retrieveLogsRequest"
        onSubmit={(e) => {
          e.preventDefault()
          void send()
        }}
      >
        <input type="hidden" className="form-control" name="device_unique" value={id} required />
        <div className="form-group">
          <label style={{ textAlign: 'left' }}>Email</label>
          <input
            className="form-control"
            type="email"
            value={email}
            name="logs_email"
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-group">
          <button type="submit" className="btn btn-theme" disabled={busy}>
            Send request
          </button>
        </div>
      </form>
    </div>
  )
}

function Banner({ page }: { page: HealthPage }) {
  const v = page.installed
  const t = page.target
  if (page.state === 'current') {
    return (
      <div className="alert alert-success">
        <i className="fas fa-check-circle"></i> Agent is up to date. Version {v}
      </div>
    )
  }
  if (page.state === 'outdated') {
    return (
      <div className="alert alert-danger">
        OptimumDesk is out-of-date. Current version is {v} while latest is {t}
      </div>
    )
  }
  if (page.state === 'beta') {
    return <div className="alert alert-warning">Beta version {v}</div>
  }
  if (page.state === 'unknown') {
    if (v === '' || v.toLowerCase() === 'undefined') {
      return <div className="alert alert-info">OptimumDesk not installed</div>
    }
    return page.isLinux ? (
      <div className="alert alert-info">OptimumDesk {v} - the latest agent version could not be read from the update server</div>
    ) : (
      <div className="alert alert-info">OptimumDesk {v} - no target agent version is set for this company</div>
    )
  }
  return <div className="alert alert-info">OptimumDesk not installed</div>
}
